// Display Banner Ads - Headliner Tactic
// Handles file uploads and form management for this specific tactic

import { createUppyUploader } from '../shared/custom_podpromote_uppy_uploader.js'

document.addEventListener('alpine:init', () => {
  Alpine.data('display_banner_ads_headliner_x_data', () => ({
    // Form data
    formData: {
      targeting: {
        locations: [],
        target_devices: [],
        podcast_genres: []
      },
      episodes: [{
        id: 'single-episode',
        episode_id: '',
        release_date: '',
        apple_link: '',
        spotify_link: ''
      }],
      visual_asset_file: null
    },
    
    // Input variables for targeting fields
    newLocation: '',
    
    init() {
      // Use $nextTick to ensure DOM is fully rendered
      this.$nextTick(() => {
        this.loadSavedConfiguration();
        this.initializeUppyUploader();
        this.initializeEpisodeSelect();
      });
      
      // Set up cleanup when component is destroyed
      this.$cleanup = () => {
        this.cleanup();
      };
    },
    
    // Load saved configuration from backend
    loadSavedConfiguration() {
      try {
        const routesDiv = this.$refs.routes;
        
        if (routesDiv && routesDiv.dataset.savedConfig) {
          const savedConfig = JSON.parse(routesDiv.dataset.savedConfig);
          
          if (savedConfig) {
            // Load targeting data
            if (savedConfig.targeting) {
              if (savedConfig.targeting.locations) {
                this.formData.targeting.locations = savedConfig.targeting.locations;
              }
              if (savedConfig.targeting.target_devices) {
                this.formData.targeting.target_devices = savedConfig.targeting.target_devices;
              }
              if (savedConfig.targeting.podcast_genres) {
                this.formData.targeting.podcast_genres = savedConfig.targeting.podcast_genres;
              }
            }
            
            // Load visual asset file data
            if (savedConfig.visual_asset_file && savedConfig.visual_asset_file.url && savedConfig.visual_asset_file.url !== 'unknown') {
              this.formData.visual_asset_file = savedConfig.visual_asset_file;
              
              // Show upload status
              this.showUploadSuccess();
              
              // Show image preview
              this.showImagePreview(savedConfig.visual_asset_file.url);
              
              // Store in hidden input
              const hiddenInput = document.getElementById('visual_asset_file_data');
              if (hiddenInput) {
                hiddenInput.value = JSON.stringify(savedConfig.visual_asset_file);
              }
            }
            
            // Load episodes data
            if (savedConfig.episodes && savedConfig.episodes.length > 0) {
              savedConfig.episodes.forEach((savedEpisode, index) => {
                if (savedEpisode.episode_id && this.formData.episodes[index]) {
                  // Update the episode data in formData
                  this.formData.episodes[index] = {
                    ...this.formData.episodes[index], // Keep existing structure
                    episode_id: savedEpisode.episode_id,
                    episode_title: savedEpisode.episode_title || savedEpisode.title || '',
                    release_date: savedEpisode.release_date || savedEpisode.pub_date || '',
                    apple_link: savedEpisode.apple_link || '',
                    spotify_link: savedEpisode.spotify_link || ''
                  };
                  
                  // Populate episode select dropdown with saved data
                  this.$nextTick(() => {
                    setTimeout(() => {
                      this.populateEpisodeSelect();
                    }, 100);
                  });
                }
              });
            }
          }
        }
      } catch (error) {
        console.error('Error loading saved configuration:', error);
      }
    },
    
    // Populate episode select dropdown with saved data
    populateEpisodeSelect() {
      this.formData.episodes.forEach((episode, index) => {
        if (episode.episode_id) {
          // Find the specific episode select element for this row
          const selectElement = document.querySelector(`select[data-episode-index="${index}"]`) || 
                               document.querySelector(`select[class*="border-gray-300"]`);
          
          if (selectElement) {
            // Set the selected value
            selectElement.value = episode.episode_id;
          }
        }
      });
    },
    
    initializeUppyUploader() {
      try {
        // Check if Uppy uploader is already initialized to prevent double initialization
        if (window.uppyInstances && window.uppyInstances['headliner-visual-asset-uploader']) {
          return;
        }
        
        // Get show ID, campaign ID, and campaign tactic ID from URL
        const showId = this.getShowId();
        const campaignId = this.getCampaignId();
        const campaignTacticId = this.getCampaignTacticId();
        
        if (!showId) {
          console.error('Show ID not found');
          return;
        }
        
        if (!campaignId) {
          console.error('Campaign ID not found');
          return;
        }
        
        if (!campaignTacticId) {
          console.error('Campaign tactic ID not found');
          return;
        }

        // Initialize Uppy uploader using the modular approach
        // All Uppy logic is outside Alpine, Alpine instance is passed for callbacks
        const uppyInstance = createUppyUploader({
          id: 'headliner-visual-asset-uploader',
          show_id: showId,
          campaign_id: campaignId,
          campaign_tactic_id: campaignTacticId,
          trigger_id: '#visual-asset-upload-trigger',
          max_file_size: 5 * 1024 * 1024, // 5MB
          allowed_file_types: ['image/jpeg', 'image/jpg', 'image/png'],
          max_number_of_files: 1,
          auto_proceed: false,
          close_after_finish: true,
          min_dimensions: { width: 300, height: 250 },
          max_dimensions: { width: 300, height: 250 },
          note: 'Accepted files: JPG, PNG. Maximum size: 5MB. Dimensions: 300x250px.',
          on_complete: (result) => {
            this.handleUploadComplete(result);
          },
          on_error: (file, error, response) => {
            this.handleUploadError(file, error, response);
          },
          alpine: this // Pass Alpine instance for callbacks
        });
        
        // Store the instance globally to prevent re-initialization
        if (!window.uppyInstances) {
          window.uppyInstances = {};
        }
        window.uppyInstances['headliner-visual-asset-uploader'] = uppyInstance;
        
      } catch (error) {
        console.error('Failed to initialize Uppy uploader:', error);
        alert('Failed to initialize uploader. Please refresh the page and try again.');
      }
    },
    
    // Cleanup function to dispose of Uppy instance
    cleanup() {
      try {
        if (window.uppyInstances && window.uppyInstances['headliner-visual-asset-uploader']) {
          const uppyInstance = window.uppyInstances['headliner-visual-asset-uploader'];
          
          // Clean up the Uppy instance
          if (uppyInstance && typeof uppyInstance.close === 'function') {
            uppyInstance.close();
          }
          
          // Remove from global registry
          delete window.uppyInstances['headliner-visual-asset-uploader'];
        }
      } catch (error) {
        console.error('Error during Uppy cleanup:', error);
      }
    },
    
    getShowId() {
      const urlParts = window.location.pathname.split('/');
      const showIndex = urlParts.indexOf('shows');
      if (showIndex !== -1 && showIndex + 1 < urlParts.length) {
        return urlParts[showIndex + 1];
      }
      return null;
    },

    getCampaignId() {
      const urlParts = window.location.pathname.split('/');
      const campaignIndex = urlParts.indexOf('campaigns');
      if (campaignIndex !== -1 && campaignIndex + 1 < urlParts.length) {
        return urlParts[campaignIndex + 1];
      }
      return null;
    },
    
    getCampaignTacticId() {
      // Try to get from the routes div first
      const routesDiv = this.$refs.routes;
      if (routesDiv && routesDiv.dataset.campaignTacticId) {
        return routesDiv.dataset.campaignTacticId;
      }
      
      // Fallback to URL parsing if needed
      const urlParts = window.location.pathname.split('/');
      const tacticIndex = urlParts.indexOf('campaign-tactics');
      if (tacticIndex !== -1 && tacticIndex + 1 < urlParts.length) {
        return urlParts[tacticIndex + 1];
      }
      
      return null;
    },
    
    // Pill management functions for targeting
    addLocation() {
      if (this.newLocation && this.newLocation.trim()) {
        const location = this.newLocation.trim();
        if (location.length > 0 && !this.formData.targeting.locations.includes(location)) {
          this.formData.targeting.locations.push(location);
        }
        this.newLocation = '';
      }
    },
    
    removeLocation(location) {
      const index = this.formData.targeting.locations.indexOf(location);
      if (index > -1) {
        this.formData.targeting.locations.splice(index, 1);
      }
    },
    
    addTargetDevice(device) {
      if (device && device.trim()) {
        const selectedDevice = device.trim();
        if (selectedDevice.length > 0 && !this.formData.targeting.target_devices.includes(selectedDevice)) {
          this.formData.targeting.target_devices.push(selectedDevice);
          
          // Reset the select dropdown
          const selectElement = document.querySelector('select[data-target-device-select]');
          if (selectElement) selectElement.value = '';
        }
      }
    },
    
    removeTargetDevice(device) {
      const index = this.formData.targeting.target_devices.indexOf(device);
      if (index > -1) {
        this.formData.targeting.target_devices.splice(index, 1);
      }
    },
    
    addPodcastGenre(genre) {
      if (genre && genre.trim()) {
        const selectedGenre = genre.trim();
        if (selectedGenre.length > 0 && !this.formData.targeting.podcast_genres.includes(selectedGenre)) {
          this.formData.targeting.podcast_genres.push(selectedGenre);
          
          // Reset the select dropdown
          const selectElement = document.querySelector('select[data-podcast-genre-select]');
          if (selectElement) selectElement.value = '';
        }
      }
    },
    
    removePodcastGenre(genre) {
      const index = this.formData.targeting.podcast_genres.indexOf(genre);
      if (index > -1) {
        this.formData.targeting.podcast_genres.splice(index, 1);
      }
    },
    
    // Handle Enter key press for any targeting field
    handleEnterKey(field) {
      switch(field) {
        case 'location':
          this.addLocation();
          break;
      }
    },
    
    handleUploadComplete(result) {
      if (result.successful && result.successful.length > 0) {
        const file = result.successful[0];
        
        // Safety check for this context
        if (!this || !this.formData) {
          return;
        }
        
        // Parse the response to get the S3 data
        let responseData;
        try {
          // The response should be the JSON from the S3 upload controller
          if (file.response) {
            // Uppy returns response in format: {status: 200, body: {...}, uploadURL: '...'}
            if (file.response.body && typeof file.response.body === 'object') {
              // S3 data is nested in the body
              responseData = file.response.body;
            } else if (typeof file.response === 'string') {
              // Fallback: try to parse as string
              responseData = JSON.parse(file.response);
            } else {
              responseData = file.response;
            }
          } else {
            responseData = {};
          }
        } catch (e) {
          console.error('Error parsing response:', e);
          responseData = {};
        }
        
        // Extract S3 data from response - the controller returns key, url, filename
        let s3Key = responseData?.key || 'unknown';
        let s3Url = responseData?.url || 'unknown';
        const filename = responseData?.filename || file.name;
        
        // Check if we got valid S3 data, if not, try alternative approaches
        if (s3Key === 'unknown' || s3Url === 'unknown') {
          // Check if S3 data is in file.response.body (Uppy format)
          if (file.response && file.response.body && typeof file.response.body === 'object') {
            if (file.response.body.key && file.response.body.key !== 'unknown') {
              s3Key = file.response.body.key;
            }
            if (file.response.body.url && file.response.body.url !== 'unknown') {
              s3Url = file.response.body.url;
            }
            if (file.response.body.filename && file.response.body.filename !== 'unknown') {
              filename = file.response.body.filename;
            }
          }
          
          // Check if S3 data is in file.response.uploadURL (alternative Uppy format)
          if (file.response && file.response.uploadURL && s3Url === 'unknown') {
            s3Url = file.response.uploadURL;
          }
          
          // Check if response is in a different format
          if (file.response && typeof file.response === 'string') {
            // Try to find S3 data in the response string
            const responseText = file.response;
            
            // Look for common S3 response patterns
            const s3KeyMatch = responseText.match(/"key"\s*:\s*"([^"]+)"/);
            const s3UrlMatch = responseText.match(/"url"\s*:\s*"([^"]+)"/);
            const filenameMatch = responseText.match(/"filename"\s*:\s*"([^"]+)"/);
            
            if (s3KeyMatch) {
              s3Key = s3KeyMatch[1];
            }
            if (s3UrlMatch) {
              s3Url = s3UrlMatch[1];
            }
            if (filenameMatch) {
              filename = filenameMatch[1];
            }
          }
          
          // If still no S3 data, check if the file object has S3 data directly
          if (file.s3_key) {
            s3Key = file.s3_key;
          }
          if (file.s3_url) {
            s3Url = file.s3_url;
          }
          
          // Final fallback: create a temporary URL for the uploaded file
          if (s3Url === 'unknown' && file.data) {
            s3Url = URL.createObjectURL(file.data);
          }
        }
        
        // If we still don't have valid S3 data, show an error
        if (s3Key === 'unknown' || s3Url === 'unknown') {
          console.error('Failed to extract S3 data from upload response');
          alert('File upload completed but S3 data could not be retrieved. Please try uploading again or contact support if the issue persists.');
          return;
        }
        
        // Update the formData with the new file information
        this.formData.visual_asset_file = {
          name: filename,
          size: file.size,
          type: file.type,
          s3_key: s3Key,
          url: s3Url
        };
        
        // Force Alpine.js reactivity by triggering a change
        this.$nextTick(() => {
          // Force reactivity by reassigning the entire formData
          this.formData = { ...this.formData };
        });
        
        // Store file data in hidden input for backend submission
        const hiddenInput = document.getElementById('visual_asset_file_data');
        if (hiddenInput) {
          // Store the complete file data as JSON for backend processing
          hiddenInput.value = JSON.stringify(this.formData.visual_asset_file);
        }
        
        // Show success message
        this.showUploadSuccess();
        
        // Show image preview
        this.showImagePreview(s3Url);
      } else if (result.failed && result.failed.length > 0) {
        // Handle failed uploads
        const failedFile = result.failed[0];
        if (failedFile && failedFile.error) {
          this.handleUploadError(failedFile, failedFile.error, failedFile.response);
        }
      }
    },
    
    handleUploadError(file, error, response) {
      // Show alert for all validation errors
      let errorMessage = error.message || 'Unknown error occurred';
      
      if (error.message && error.message.includes('dimensions')) {
        errorMessage = `Image Upload Error: ${error.message}\n\nPlease upload an image that is exactly 300x250 pixels.`;
      } else if (error.message && error.message.includes('size')) {
        errorMessage = `File Size Error: ${error.message}\n\nPlease upload a file smaller than 5MB.`;
      } else if (error.message && error.message.includes('type')) {
        errorMessage = `File Type Error: ${error.message}\n\nPlease upload a JPG or PNG file.`;
      }
      
      alert(errorMessage);
      this.showUploadError(error.message || 'Upload failed');
    },
    
    showUploadSuccess() {
      // Show success message to user
      const statusElement = document.getElementById('visual-asset-upload-status');
      
      if (statusElement) {
        statusElement.classList.remove('hidden');
        
        const messageElement = statusElement.querySelector('span');
        
        if (messageElement) {
          const successMessage = `${this.formData.visual_asset_file.name} uploaded successfully`;
          messageElement.textContent = successMessage;
        }
        
        // Ensure icon is green
        const iconElement = statusElement.querySelector('svg');
        if (iconElement) {
          iconElement.classList.remove('text-red-500');
          iconElement.classList.add('text-green-500');
        }
      }
    },
    
    showImagePreview(imageUrl) {
      // Show image preview
      const previewElement = document.getElementById('visual-asset-preview');
      const previewImage = document.getElementById('preview-image');
      
      if (previewElement && previewImage) {
        previewImage.src = imageUrl;
        previewElement.classList.remove('hidden');
      }
    },
    
    showUploadError(message) {
      // Show error message to user
      const statusElement = document.getElementById('visual-asset-upload-status');
      if (statusElement) {
        statusElement.classList.remove('hidden');
        const messageElement = statusElement.querySelector('span');
        if (messageElement) {
          messageElement.textContent = message;
        }
        // Change icon to red X
        const iconElement = statusElement.querySelector('svg');
        if (iconElement) {
          iconElement.classList.remove('text-green-500');
          iconElement.classList.add('text-red-500');
        }
      }
      
      // Also show alert for validation errors
      if (message && (message.includes('dimensions') || message.includes('size') || message.includes('type'))) {
        alert(message);
      }
    },
    
    // Episode management - simplified for single episode
    initializeEpisodeSelect() {
      // Alpine.js handles the episode selection via @change directive
      // No manual event listeners needed
    },

    handleEpisodeSelection(episodeId, index) {
      if (episodeId && this.formData.episodes[index]) {
        this.formData.episodes[index].episode_id = episodeId;
        
        // Find the selected episode data from the backend episodes
        const selectedEpisode = this.findEpisodeById(episodeId);
        
        if (selectedEpisode) {
          // Handle pub_date
          if (selectedEpisode.pub_date) {
            const formattedDate = this.formatDate(selectedEpisode.pub_date);
            this.formData.episodes[index].release_date = formattedDate;
          } else {
            this.formData.episodes[index].release_date = 'Release date not available';
          }
          
          // Handle distribution platforms
          const platforms = selectedEpisode.distribution_platforms;
          
          // Set Apple link using itunes_url key
          if (platforms.itunes_url) {
            this.formData.episodes[index].apple_link = platforms.itunes_url;
          } else {
            this.formData.episodes[index].apple_link = '';
          }
          
          // Set Spotify link using spotify_url key
          if (platforms.spotify_url) {
            this.formData.episodes[index].spotify_link = platforms.spotify_url;
          } else {
            this.formData.episodes[index].spotify_link = '';
          }
        } else {
          this.formData.episodes[index].apple_link = '';
          this.formData.episodes[index].spotify_link = '';
        }
      }
    },

    // Find episode by ID from the backend data
    findEpisodeById(episodeId) {
      // Get episodes data from the global window object
      let episodesData = window.episodesData || [];
      
      if (!episodesData || episodesData.length === 0) {
        return null;
      }
      
      const foundEpisode = episodesData.find(episode => episode.id == episodeId);
      return foundEpisode;
    },

    // Format date for display
    formatDate(dateString) {
      if (!dateString) return 'Date not available';
      
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid date';
        
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'Date not available';
      }
    },
      
    // Clear uploaded file data
    clearFileData() {
      this.formData.visual_asset_file = null;
      const hiddenInput = document.getElementById('visual_asset_file_data');
      if (hiddenInput) {
        hiddenInput.value = '';
      }
      const statusElement = document.getElementById('visual-asset-upload-status');
      if (statusElement) {
        statusElement.classList.add('hidden');
      }
      const previewElement = document.getElementById('visual-asset-preview');
      if (previewElement) {
        previewElement.classList.add('hidden');
      }
    },
    
    // Form actions
    saveForm() {
      // Validate required fields using DOM-based validation
      const uploadStatusElement = document.getElementById('visual-asset-upload-status');
      if (!uploadStatusElement || uploadStatusElement.classList.contains('hidden')) {
        alert('Please upload a visual asset before saving.');
        return;
      }
      
      if (!this.formData.targeting.locations.length) {
        alert('Please select at least one location for targeting.');
        return;
      }
      
      if (!this.formData.targeting.podcast_genres.length) {
        alert('Please select at least one podcast genre for targeting.');
        return;
      }
      
      if (!this.formData.episodes[0].episode_id) {
        alert('Please select an episode to highlight.');
        return;
      }
      
      // Get the submit link from the routes div
      const routesDiv = this.$refs.routes;
      if (!routesDiv) {
        console.error('Routes div not found');
        return;
      }
      
      const campaignTacticId = routesDiv.dataset.campaignTacticId;
      if (!campaignTacticId) {
        console.error('Campaign tactic ID not found');
        return;
      }
      
      // Construct the submit URL manually like AudienceLift does
      const urlParts = window.location.pathname.split('/');
      const showIndex = urlParts.indexOf('shows');
      const campaignIndex = urlParts.indexOf('campaigns');
      
      if (showIndex === -1 || campaignIndex === -1) {
        alert('Invalid URL structure. Please refresh the page and try again.');
        return;
      }
      
      const showId = urlParts[showIndex + 1];
      const campaignId = urlParts[campaignIndex + 1];
      
      // Construct the campaign tactic endpoint URL
      const submitUrl = `/shows/${showId}/campaigns/${campaignId}/campaign-tactics/${campaignTacticId}`;
      
      // Get file data from hidden input
      const hiddenInput = document.getElementById('visual_asset_file_data');
      
      let visualAssetFile = {};
      if (hiddenInput && hiddenInput.value) {
        try {
          visualAssetFile = JSON.parse(hiddenInput.value);
        } catch (e) {
          console.error('Error parsing file data:', e);
        }
      }
      
      // Prepare form data for submission to campaign_tactics#update
      // The controller expects params[:form_data] with targeting and file data
      const formData = {
        targeting: {
          locations: this.formData.targeting.locations,
          target_devices: this.formData.targeting.target_devices,
          podcast_genres: this.formData.targeting.podcast_genres
        },
        visual_asset_file: visualAssetFile,
        episodes: this.formData.episodes.filter(episode => episode.episode_id).map(ep => ({
          episode_id: ep.episode_id,
          release_date: ep.release_date,
          apple_link: ep.apple_link,
          spotify_link: ep.spotify_link
        }))
      };
      
      // Set the final URL for form submission
      const finalUrl = submitUrl;
      
      // Create a hidden form
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = finalUrl;
      form.style.display = 'none';
      
      // Add CSRF token
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      form.appendChild(csrfInput);
      
      // Add method override for PATCH
      const methodInput = document.createElement('input');
      methodInput.type = 'hidden';
      methodInput.name = '_method';
      methodInput.value = 'patch';
      form.appendChild(methodInput);
      
      // Add form data
      const formDataInput = document.createElement('input');
      formDataInput.type = 'hidden';
      formDataInput.name = 'form_data';
      formDataInput.value = JSON.stringify(formData);
      form.appendChild(formDataInput);
      
      // Add to DOM and submit
      document.body.appendChild(form);
      form.submit();
      
      // Comment out XMLHttpRequest for now
      /*
      const xhr = new XMLHttpRequest();
      xhr.open('PATCH', finalUrl, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('X-CSRF-Token', document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '');
      
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200 || xhr.status === 302) {
            // Check if the response is a redirect (status 302)
            if (xhr.status === 302) {
              // Get the redirect URL from the response headers
              const redirectUrl = xhr.getResponseHeader('Location');
              if (redirectUrl) {
                window.location.href = redirectUrl;
              } else {
                // Simple redirect to step 3
                const step3Url = submitUrl.replace('/campaign-tactics/', '/').replace(/\/[^\/]+$/, '/step_3');
                window.location.href = step3Url;
              }
            } else {
              // Try to parse JSON response
              try {
                const data = JSON.parse(xhr.responseText);
                alert('Configuration saved successfully!');
              } catch (e) {
                alert('Configuration saved successfully!');
              }
            }
          } else {
            console.error('XHR Error Status:', xhr.status);
            console.error('XHR Error Response:', xhr.responseText);
            alert('Error saving configuration. Please try again.');
          }
        }
      };
      
      xhr.onerror = () => {
        console.error('XHR Network Error');
        alert('Network error. Please try again.');
      };
      
      xhr.send(JSON.stringify({ form_data: formData }));
      */
    },

    // Save as draft (no validations)
    saveAsDraft() {
        // Get file data from hidden input
        const hiddenInput = document.getElementById('visual_asset_file_data');
        
        let visualAssetFile = {};
        if (hiddenInput && hiddenInput.value) {
          try {
            visualAssetFile = JSON.parse(hiddenInput.value);
          } catch (e) {
            console.error('Error parsing file data:', e);
          }
        }
        
        // Prepare data for submission (same as saveForm but without validations)
        const formDataToSubmit = {
          targeting: {
            locations: this.formData.targeting.locations,
            target_devices: this.formData.targeting.target_devices,
            podcast_genres: this.formData.targeting.podcast_genres
          },
          visual_asset_file: visualAssetFile,
          episodes: this.formData.episodes.filter(episode => episode.episode_id).map(ep => ({
          episode_id: ep.episode_id,
          release_date: ep.release_date,
          apple_link: ep.apple_link,
          spotify_link: ep.spotify_link
        }))
        };
        
        // Get the save as draft link from the routes template
        const routesTemplate = this.$refs.routes;
        const saveAsDraftLink = routesTemplate?.dataset?.saveAsDraftLink;
        
        // Create a hidden form and submit it
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = saveAsDraftLink;
        form.style.display = 'none';
        
        // Add CSRF token
        const csrfInput = document.createElement('input');
        csrfInput.type = 'hidden';
        csrfInput.name = 'authenticity_token';
        csrfInput.value = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
        form.appendChild(csrfInput);
        
        // Add method override for PATCH
        const methodInput = document.createElement('input');
        methodInput.type = 'hidden';
        methodInput.name = '_method';
        methodInput.value = 'patch';
        form.appendChild(methodInput);
        
        // Add form data
        const formDataInput = document.createElement('input');
        formDataInput.type = 'hidden';
        formDataInput.name = 'form_data';
        formDataInput.value = JSON.stringify(formDataToSubmit);
        form.appendChild(formDataInput);
        
        // Add to DOM and submit
        document.body.appendChild(form);
        form.submit();
    }
  }));
});
